import { bindPageLayout } from "util/pageLayout";
import { IDENTITY_ROLES } from "constants/memberTypes";
import {Routes, Route, useLocation, useNavigate, matchRoutes} from "react-router-dom";

import PublicRoutes from "routes/PublicRoutes";
import PrivateRoutes from "routes/PrivateRoutes";

import NotFound from "../pages/NotFound";
import {useEffect, useRef, Suspense} from "react";
import RouteChunkBoundary from "components/ui/RouteChunkBoundary";
import {useAuth} from "context/AuthContext";
import {callAddRole, callGetMyRoles, callSubmitRoleVerification, callRemoveRole, callCreateOrder, callAgreeTerms} from "api/MuyeonApi";
import {hasNativeBridge} from "util/userAgent";
import {setFloatingHidden, notifyRoute, openSignupTerms, openRoleVerification, notifyWebReady, notifyRolesChanged} from "common/nativeBridge";
import {RESULT_KEYS, emitResult} from "common/resultKeys";
import {openNativeOrWeb} from "common/nativeRoute";
import {matchNativeLink} from "common/nativeLink";
import {getConsentMode, setConsentMode, stashSignupAgreements} from "util/consent";
import { SPACE_FEATURE_ENABLED, SPACE_RENTAL_ENABLED } from "constants/featureFlags";
import { takeFunnelRedirect } from "util/pendingPosting";
import { showToast } from "util/appToast";
import { privateRouteDefs } from "routes";
import { startAppAnalytics, trackScreen } from "util/appAnalytics";
import { pixelPageView } from "util/metaPixel";

// 회원유형별 온보딩 후 랜딩 화면
const ROLE_LANDING = {
  HOBBY: "/lessons",
  SPACE: SPACE_FEATURE_ENABLED ? "/spaces" : "/home",
  ACADEMY: "/jobs",
  TEAM: "/jobs",
  TEACHER: "/jobs",
  DANCER: "/jobs",
};
// 관리자 인증(서류 심사)이 필요한 회원유형. 일반회원·강사·무용수는 즉시 추가된다.
//  ★ 2026-10-12 강사·무용수는 본인 인증으로 바로 부여(서버 IDENTITY_REQUIRED_ROLES).
const VERIFICATION_REQUIRED_ROLES = ["ACADEMY", "TEAM"];

const AppRouter = () => {

  const location = useLocation();
  // 약관 게이트로 띄운 네이티브 동의 화면이 떠 있는 동안 true — 같은 화면을 겹쳐 띄우지 않는다.
  //  ★ 2026-10-04: 소셜 신규가입 → 휴대폰 본인확인(외부 페이지)에서 뒤로 오면 웹이 새로 뜨고,
  //    저장된 user → /auth/me 갱신으로 user 가 두 번 바뀌어 게이트가 두 번 돌아 동의 화면이 2장 쌓였다.
  //    네이티브는 같은 화면 중복을 막지 않으므로 웹에서 한 번만 연다. 동의·거절 콜백이나 제외 경로에서 푼다.
  const termsGateOpen = useRef(false);
  const navigate = useNavigate();
  const { user, activeType, updateUser, setActiveType, logout } = useAuth();
  // 링크 → 네이티브 화면(__openLink)이 최신 로그인·활성유형을 보게 한다(effect 를 다시 심지 않고).
  const linkCtx = useRef({});
  linkCtx.current = { user, activeType };

  // 네이티브 딥링크 → 메인 앱 라우터로 이동(모달 대신). 하단 탭·뒤로가기 정상 동작.
  //   네이티브(CustomAppDelegate)가 푸시 url 의 경로로 window.__nativeGo(path) 호출.
  useEffect(() => {
    window.__nativeGo = (path) => {
      try {
        if (!path) return;
        // 구버전 푸시·딥링크도 공간 화면으로 우회 진입하지 못하게 한다.
        //  ★ 2026-11-03 공간 대관 재오픈(SPACE_RENTAL_ENABLED) — 예전 공간 문의 화면만 막는다.
        if (/^\/mySpaceInquiries(\/|$)/.test(path) || (!SPACE_RENTAL_ENABLED && /^\/(spaces|mySpaces|mySpaceReservations|mySpaceBookings)(\/|$)/.test(path))) {
          navigate("/home", { replace: true });
          return;
        }
        // 딥링크가 히스토리 최상단(뒤로갈 곳 없음)이고 홈이 아니면, 홈을 밑에 깔아
        //  뒤로가기 제스처(웹뷰 히스토리)와 < 버튼 모두 홈으로 복귀 가능하게 한다.
        const idx = window.history.state?.idx ?? 0;
        if (idx === 0 && path !== "/home" && path !== "/") {
          navigate("/home", { replace: true });
        }
        navigate(path);
      } catch (e) { /* noop */ }
    };
    // ★ 2026-10-26 링크(유니버셜 링크·앱 링크·알림톡) 전용 입구 — 네이티브 화면이 있는 주소면 그 화면을 연다(common/nativeLink 표).
    //  없거나 보호 장치(로그인·유형)를 통과 못 하면 __nativeGo 와 같다(웹 페이지 + 웹의 원래 안내).
    //  네이티브는 지금 화면 위에 뜬다. 히스토리 첫 칸이면 그 자리를 홈으로 바꿔 닫았을 때 홈이 보이게 한다.
    window.__openLink = (path) => {
      try {
        if (!path) return;
        const open = matchNativeLink(path, { navigate, routeDefs: privateRouteDefs, ...linkCtx.current });
        if (!open) { window.__nativeGo?.(path); return; }
        const idx = window.history.state?.idx ?? 0;
        if (idx === 0 && window.location.pathname !== "/home") navigate("/home", { replace: true });
        open();
      } catch (e) { /* noop */ }
    };
    return () => {
      try { delete window.__nativeGo; } catch (e) { /* noop */ }
      try { delete window.__openLink; } catch (e) { /* noop */ }
    };
  }, [navigate]);

  // 네이티브 결과 콜백 단일 진입점 — 앱이 화면을 닫거나 데이터를 바꾸면 __muyeonResult(key, payload).
  //  화면들은 useResultRefresh 로 키를 구독한다(전역 슬롯을 화면마다 덮어쓰던 충돌 제거).
  //  옛 앱 빌드는 아래 레거시 __onX 이름을 부르므로 같은 키로 이어준다(여기가 유일한 소유자).
  useEffect(() => {
    const K = RESULT_KEYS;
    const hooks = {
      __muyeonResult: (key, payload) => emitResult(key, payload),
      __onNativeReservationsChanged: () => emitResult(K.RESERVATIONS),
      // 구버전 앱·AOS 취소 콜백 — MyReservations 가 canceledId 로 즉시 취소선을 긋고 재조회한다.
      __onLessonReservationCanceled: (id) => emitResult(K.RESERVATIONS, { canceledId: id }),
      __onNativeReviewWritten: () => emitResult(K.REVIEWS),
      __onAutoQuoteChanged: () => emitResult(K.AUTO_QUOTE),
      __onNativeNotificationsRead: () => emitResult(K.NOTIFICATIONS),
      __onNativeResumeChanged: () => emitResult(K.RESUME),
      __refreshMyLessons: () => emitResult(K.LESSONS),
      __onNativeJobChanged: () => emitResult(K.JOB_POSTINGS),
      __onNativeAcademyChanged: () => emitResult(K.ACADEMY),
    };
    Object.entries(hooks).forEach(([name, fn]) => { window[name] = fn; });
    // 앱에게 "이제 __muyeonResult 받을 수 있음" 1회 통지(AOS 재시도 루프 종료용). 앱 밖이면 무시된다.
    if (hasNativeBridge()) notifyWebReady();
    return () => {
      Object.entries(hooks).forEach(([name, fn]) => {
        try { if (window[name] === fn) delete window[name]; } catch (e) { /* noop */ }
      });
    };
  }, []);

  // 웹 모달(하단시트/딤드)이 열리면 네이티브 플로팅(채팅·인증책갈피)을 숨김 → 모달 X버튼 가림 방지.
  useEffect(() => {
    if (!hasNativeBridge()) return;
    let last = null;
    const check = () => {
      const open = !!document.querySelector(".modal-dimmed.is-show, .modal-bottom-dimmed.is-show");
      if (open !== last) { last = open; setFloatingHidden(open); }
    };
    const obs = new MutationObserver(check);
    obs.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["class"] });
    check();
    return () => { obs.disconnect(); setFloatingHidden(false); };
  }, []);

  // 현재 라우트를 네이티브에 통지 → 네이티브가 플로팅 채팅버튼 노출 화이트리스트(최상위 탭만) 판정.
  useEffect(() => {
    if (!hasNativeBridge()) return;
    notifyRoute(location.pathname);
  }, [location.pathname]);

  // 네이티브 회원유형 온보딩(가입완료 후) 완료 콜백 → 역할 추가 + (사업역할은 인증 첨부화면).
  useEffect(() => {
    // 역할 관련 콜백(제출·추가·해제)이 모두 여기를 지난다 — 끝나면 네이티브 플로팅에 1회 재조회 신호.
    const refreshRoles = async () => {
      try {
        const rr = await callGetMyRoles();
        if (Array.isArray(rr?.data?.held)) {
          updateUser({ memberTypes: rr.data.held, activeType: rr.data.activeType });
          return rr.data;
        }
      } catch (e) { /* noop */ }
      finally { if (hasNativeBridge()) notifyRolesChanged(); }
      return null;
    };
    // 서류 제출. 실제 제출했으면 true 반환(제출완료 팝업 트리거용), 서버가 바로 승인했으면 "approved".
    //  ★ 2026-10-22 학원은 국세청 확인 번호면 자동 승인 — 그때는 '접수' 팝업 대신 네이티브 플로팅의 인증 완료 화면이 뜬다
    //  (refreshRoles 의 rolesChanged 신호 → 승인 미확인 상태 재조회). 서류 없이 번호만 오는 앱(1.0.6~)도 제출한다.
    //  businessStartDate(개업일자): 1.0.6~ 앱이 5번째 인자로 넘긴다 — 옛 앱은 없어 관리자 심사로 간다.
    const submitDocs = async (role, urlsJson, academyName, businessRegistrationNumber, businessStartDate) => {
      let urls = [];
      // 인자는 JSON 문자열이 계약이지만, 배열을 그대로 넘기는 앱(구 Android 빌드)이 이미 배포돼 있다.
      // 그 경우 JSON.parse 가 실패해 서류가 한 장도 제출되지 않으므로 배열도 그대로 받아준다.
      if (Array.isArray(urlsJson)) urls = urlsJson;
      else { try { urls = JSON.parse(urlsJson || "[]"); } catch (e) { /* noop */ } }
      if (!Array.isArray(urls)) urls = [];
      if (urls.length || (role === "ACADEMY" && businessRegistrationNumber)) {
        const documents = urls.map((url) => ({ type: "DOC", url }));
        // 학원 인증이면 첨부화면에서 입력한 학원명도 함께 저장(프로필 academyName 단일 소스)
        try {
          const r = await callSubmitRoleVerification(
            role,
            documents,
            academyName || undefined,
            businessRegistrationNumber || undefined,
            businessStartDate || undefined,
          );
          return r?.data?.autoApproved === true ? "approved" : true;
        } catch (e) { /* noop */ }
      }
      return false;
    };
    // 온보딩 완료(네이티브가 역할선택+인증첨부까지 처리) → 역할 추가 + 인증서류 제출 + 라우팅.
    //  인증대상+서류제출 시 마이페이지 + 제출완료 팝업, 건너뛰기면 마이페이지, 일반회원은 유형별 랜딩.
    window.__onRoleComplete = async (role, urlsJson, academyName, businessRegistrationNumber, businessStartDate) => {
      if (!SPACE_FEATURE_ENABLED && role === "SPACE") { navigate("/home", { replace: true }); return; }
      try { await callAddRole(role); } catch (e) { /* noop */ }
      const submitted = await submitDocs(role, urlsJson, academyName, businessRegistrationNumber, businessStartDate);
      await refreshRoles();
      let to;
      // 광고 유입 공고 작성 중 가입한 경우 — 서류 제출 뒤 작성 화면으로 돌아가 인증 대기 공고로 자동 등록.
      const funnel = submitted ? takeFunnelRedirect() : null;
      if (funnel) to = funnel;
      else if (submitted === "approved") to = "/mypage";
      // ★ 2026-10-12 강사·무용수는 서버가 바로 부여한다(구버전 앱이 낸 서류는 '분류 전' 전문 정보 확인으로 보관) — 심사 안내 없이 랜딩.
      else if (IDENTITY_ROLES.includes(role)) to = ROLE_LANDING[role] || "/main";
      else if (submitted) to = "/mypage?verifySubmitted=1";
      else if (VERIFICATION_REQUIRED_ROLES.includes(role)) to = "/mypage";
      else to = ROLE_LANDING[role] || "/main";
      try { navigate(to, { replace: true }); } catch (e) { /* noop */ }
    };
    // 인증 유도 팝업 "인증하기" → 네이티브 첨부화면 완료 콜백(역할은 이미 보유). 서류 제출 후 마이페이지.
    window.__onRoleVerify = async (role, urlsJson, academyName, businessRegistrationNumber, businessStartDate) => {
      const submitted = await submitDocs(role, urlsJson, academyName, businessRegistrationNumber, businessStartDate);
      await refreshRoles();
      // 인증 대기 공고 작성에서 연 인증 화면이면 작성 화면으로 돌아가 자동 등록(util/pendingPosting).
      const funnel = submitted ? takeFunnelRedirect() : null;
      const done = IDENTITY_ROLES.includes(role) || submitted === "approved" ? "/mypage" : "/mypage?verifySubmitted=1"; // ★ 2026-10-12 강사·무용수는 즉시 부여
      try { navigate(funnel || (submitted ? done : "/mypage"), { replace: true }); } catch (e) { /* noop */ }
    };
    // 인증 승인 완료화면 '시작하기'(네이티브) → 역할 갱신 + 해당 유형 화면으로 즉시 전환.
    window.__onRoleApproved = async (role) => {
      const latest = await refreshRoles();
      if (latest?.held?.includes(role)) setActiveType(role);
      try { navigate(ROLE_LANDING[role] || "/main", { replace: true }); } catch (e) { /* noop */ }
    };
    // 네이티브 회원유형 관리 화면 '해제' → 역할 제거 + 갱신. (라우팅 없음: 네이티브 화면 유지)
    window.__onRoleRemove = async (role) => {
      try { await callRemoveRole(role); } catch (e) { /* noop */ }
      await refreshRoles();
    };
    // 네이티브 관리 화면 '추가/전환'(개인역할·승인완료 사업역할) → 역할 추가 + 갱신. 라우팅 없음.
    window.__onRoleManageAdd = async (role) => {
      if (!SPACE_FEATURE_ENABLED && role === "SPACE") return;
      try { await callAddRole(role); } catch (e) {
        // 본인인증 전(409)은 전역 IdentityRequiredHost 가 안내한다. 그 밖의 실패를 삼키면 앱은 '추가됨'으로
        // 보이는데 서버엔 없어서, 다음 기능 진입 때 '유형을 추가할까요?'가 다시 떴다(2026-10-19).
        if (e?.response?.data?.code !== "IDENTITY_REQUIRED") showToast("회원유형을 추가하지 못했어요. 잠시 후 다시 시도해 주세요.");
      }
      const latest = await refreshRoles();
      // 인증 완료 이력이 있어 다시 보유한 유형은 저장 완료 후 정확히 그 유형으로 전환한다.
      // 미승인 유형은 held에 없으므로 일반회원 상태를 유지하고 인증 화면만 이용할 수 있다.
      if (latest?.held?.includes(role)) {
        setActiveType(role);
      } else if (IDENTITY_ROLES.includes(role)) {
        // ★ 2026-10-12 강사·무용수가 부여되지 않았다 = 본인인증 전(예전 가입자). 안내는 전역 IdentityRequiredHost 가 띄운다.
      } else if (VERIFICATION_REQUIRED_ROLES.includes(role)) {
        // 강사·무용수 인증 도입 전 앱도 서버에서 바로 부여되지 않으면 인증 화면으로 이어준다.
        // 최신 앱은 __onRoleManageVerify 를 직접 호출하므로 이 경로와 중복되지 않는다.
        openRoleVerification(role);
      }
    };
    // 네이티브 관리 화면 '인증 서류 제출'(사업역할) → 서류 제출(심사중) + 갱신 + 제출완료 안내.
    //  최신 앱은 제출과 동시에 관리 화면까지 닫으므로, 웹이 결과를 알려주지 않으면
    //  사용자는 화면만 사라지고 제출됐는지 알 수 없다. __onRoleVerify 와 같은 안내로 이어준다.
    //  (관리 화면을 열어두는 구버전 앱에서는 화면 뒤에서 이동해 두었다가 닫을 때 안내가 보인다.)
    window.__onRoleManageVerify = async (role, urlsJson, academyName, businessRegistrationNumber, businessStartDate) => {
      const submitted = await submitDocs(role, urlsJson, academyName, businessRegistrationNumber, businessStartDate);
      await refreshRoles();
      if (submitted) {
        const funnel = takeFunnelRedirect();
        try { navigate(funnel || (submitted === "approved" ? "/mypage" : "/mypage?verifySubmitted=1"), { replace: true }); } catch (e) { /* noop */ }
      }
    };
    // 네이티브 약관동의 화면 '동의하고 계속' → mode 에 따라 가입 진행 or 게이트 저장.
    window.__onSignupTermsAgreed = async (agreementsJson) => {
      let a = {};
      try { a = typeof agreementsJson === "string" ? JSON.parse(agreementsJson) : (agreementsJson || {}); } catch (e) { /* noop */ }
      termsGateOpen.current = false;
      if (getConsentMode() === "gate") {
        try { await callAgreeTerms(a); updateUser({ termsAgreed: true, termsReconsent: false }); } catch (e) { /* noop */ }
        try { navigate("/home", { replace: true }); } catch (e) { /* noop */ }
      } else {
        stashSignupAgreements(a);
        try { navigate("/register"); } catch (e) { /* noop */ }
      }
    };
    // 네이티브 약관동의 '뒤로/미동의' → 로그인으로 복귀(게이트면 로그아웃까지).
    window.__onSignupTermsDeclined = () => {
      termsGateOpen.current = false;
      if (getConsentMode() === "gate") { try { logout(); } catch (e) { /* noop */ } }
      try { navigate("/login", { replace: true }); } catch (e) { /* noop */ }
    };
    // 네이티브 '활동 유형 전환' 콜백(window.__onActiveTypeChanged)은 AuthContext 가 등록한다 — 앱이 이미 서버에 저장한
    //  값이라 다시 PATCH 하지 않아야 한다. 여기서 setActiveType 으로 덮어쓰면 그 보호가 사라진다(2026-10-20).
    // 네이티브 멤버십 화면에서 플랜 선택 → 웹에서 주문 생성(추후 웹PG 결제창 연결). scopeRefId 선택.
    window.__onMembershipSelected = async (planCode, scopeRefId) => {
      try {
        await callCreateOrder({ planCode, ...(scopeRefId ? { scopeRefId: Number(scopeRefId) } : {}) });
        // TODO(P4): 여기서 웹PG(이니시스 등) 결제창 오픈. 현재는 주문(PENDING) 생성.
      } catch (e) { /* noop */ }
    };
    return () => {
      try { delete window.__onRoleComplete; } catch (e) { /* noop */ }
      try { delete window.__onRoleVerify; } catch (e) { /* noop */ }
      try { delete window.__onRoleApproved; } catch (e) { /* noop */ }
      try { delete window.__onRoleRemove; } catch (e) { /* noop */ }
      try { delete window.__onRoleManageAdd; } catch (e) { /* noop */ }
      try { delete window.__onRoleManageVerify; } catch (e) { /* noop */ }
      try { delete window.__onMembershipSelected; } catch (e) { /* noop */ }
      try { delete window.__onSignupTermsAgreed; } catch (e) { /* noop */ }
      try { delete window.__onSignupTermsDeclined; } catch (e) { /* noop */ }
    };
  }, [navigate, updateUser, setActiveType, logout]);

  // 소셜 가입 미완료 게이트 — 카카오·Apple 로 계정만 생기고 추가정보(본인확인·회원유형)를 끝내지 않았으면
  //  어디로 가든 /social-signup 으로 되돌린다. (2026-10-04: 본인확인에서 뒤로 나오면 가입 단계를 통째로 건너뛰었다)
  //  약관 게이트보다 먼저 — 미완료 상태에서 약관만 받고 홈으로 보내지 않는다.
  useEffect(() => {
    if (!user?.signupIncomplete) return;
    const p = location.pathname;
    if (["/social-signup", "/login", "/oauth/kakao", "/naverAuth", "/policy", "/signupTerms"].includes(p)) return;
    navigate(`/social-signup?redirect=${encodeURIComponent(p === "/" ? "/home" : p)}`, { replace: true });
  }, [user, location.pathname, navigate]);

  // 약관 미동의 게이트 — 로그인된 유저가 필수 약관 미동의(termsAgreed===false)면 동의화면 강제.
  //  소셜 로그인은 계정이 먼저 생겨 동의 전이므로 진입 시마다 여기서 다시 뜬다.
  useEffect(() => {
    if (!user || user.termsAgreed !== false) { termsGateOpen.current = false; return; }
    if (user.signupIncomplete) return; // 가입(추가정보)부터 — 위 게이트가 /social-signup 으로 보낸다 // 명시적 false 일 때만(구버전 캐시=undefined 는 제외)
    const p = location.pathname;
    // /policy 는 동의 화면의 '보기'가 여는 약관 전문이다 — 빼면 보기를 누르자마자 동의 화면으로 되돌아왔다.
    if (["/signupTerms", "/login", "/register", "/social-signup", "/oauth/kakao", "/naverAuth", "/", "/policy"].includes(p)) { termsGateOpen.current = false; return; }
    if (termsGateOpen.current) return; // 이미 띄웠다 — user 갱신·경로 변화로 다시 들어와도 한 번만
    termsGateOpen.current = true;
    setConsentMode("gate");
    openNativeOrWeb(openSignupTerms, { mode: "gate", redirect: p }, navigate, { replace: true });
  }, [user, location.pathname, navigate]);

  useEffect(() => bindPageLayout(), [location.pathname]);

  // 이용 행동 로그 — 화면 진입(라우트 패턴 /teachers/:id 로 묶어 센다). 앱 안 웹페이지도 여기서 잡힌다.
  useEffect(() => { startAppAnalytics(); }, []);
  useEffect(() => {
    const matches = matchRoutes([{ path: "/", children: privateRouteDefs }], location.pathname);
    const pattern = matches
      ? "/" + matches.map((m) => m.route.path).filter((x) => x && x !== "/").join("/")
      : location.pathname;
    trackScreen(pattern === "/" ? location.pathname : pattern);
    pixelPageView(); // Meta 픽셀(광고 유입 측정) — ID 없거나 앱 안이면 아무것도 안 한다
  }, [location.pathname]);

  return (
    <RouteChunkBoundary routeKey={location.pathname}>
    <Suspense fallback={<div className="page" role="status" aria-label="Loading" style={{ minHeight: "30vh" }} />}>
    <Routes>
      {/* 공개 라우트 (로그인/회원가입/스타트) */}
      {PublicRoutes}

      {/* 앱 라우트 (열람 공개 + 일부 로그인 보호) */}
      {PrivateRoutes}

      {/* 없는 경로 -> 404 페이지 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </RouteChunkBoundary>
  );
};

export default AppRouter;
